// Modules
import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Rxjs
import { Observable } from 'rxjs';

// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Actions
import * as ForcedPasswordChangeActions from '@store/actions/forced-password-change.actions';
// Store - Selectors
import * as ForcedPasswordChangeSelectors from '@store/selectors/forced-password-change.selectors';

// Validators
import { passwordComplexityValidator } from '@shared/validators/password-complexity.validator';
import { passwordMatchValidator } from '@shared/validators/password-match.validator';

@Component({
  standalone: false,
  selector: 'app-forced-password-change',
  templateUrl: './forced-password-change.component.html',
  styleUrls: ['./forced-password-change.component.scss'],
})
export class ForcedPasswordChangeComponent implements OnInit, OnDestroy {
  // Reactive form
  public passwordForm = this._formBuilder.group({
    newPassword: ['', [Validators.required, passwordComplexityValidator()]],
    newPasswordRepeat: [
      '',
      [Validators.required, passwordMatchValidator('newPassword')],
    ],
  });

  // Observables from store
  public changing$: Observable<boolean> = this._store.pipe(
    select(ForcedPasswordChangeSelectors.selectForcedPasswordChangeChanging)
  );
  public challengeId$: Observable<string | null> = this._store.pipe(
    select(ForcedPasswordChangeSelectors.selectForcedPasswordChangeChallengeId)
  );

  // Properties
  private _challengeId: string = '';
  public showBanner: boolean = false;

  // Error arrays for custom-text-field
  public newPasswordErrors = [
    {
      type: 'hasUppercase',
      message: 'La contraseña no cumple con todos los requisitos',
    },
    {
      type: 'hasLowercase',
      message: 'La contraseña no cumple con todos los requisitos',
    },
    {
      type: 'hasNumber',
      message: 'La contraseña no cumple con todos los requisitos',
    },
    {
      type: 'hasSpecialChar',
      message: 'La contraseña no cumple con todos los requisitos',
    },
    {
      type: 'minLength',
      message: 'La contraseña no cumple con todos los requisitos',
    },
    {
      type: 'maxLength',
      message: 'La contraseña excede el largo máximo permitido',
    },
    // Populated dynamically from the server's validation message (see fieldError$ subscription)
    { type: 'serverError', message: '' },
  ];

  public repeatPasswordErrors = [
    { type: 'passwordMismatch', message: 'Las contraseñas no coinciden' },
  ];

  // Constructor
  constructor(
    private _store: Store<AppState>,
    private _formBuilder: FormBuilder,
    private _destroyRef: DestroyRef,
    private _actions$: Actions
  ) {}

  // Lifecycle hooks
  ngOnInit(): void {
    // Subscribe to challengeId$ → store in _challengeId
    this.challengeId$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(challengeId => {
        this._challengeId = challengeId || '';
      });

    // Subscribe to newPassword value changes → trigger repeat field revalidation
    this.passwordForm
      .get('newPassword')
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.passwordForm.get('newPasswordRepeat').updateValueAndValidity();
      });

    // Subscribe to changing$ → disable/enable form based on loading state
    this.changing$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(changing => {
        if (changing) {
          this.passwordForm.disable();
        } else {
          this.passwordForm.enable();
        }
      });

    // Listen for the backend validation message and show it under the field
    // (e.g. same-as-previous, contains name/document/email). This listens to
    // the action directly (not a store selector) so it can't race with the
    // changing$ subscription above: both are derived from the same
    // forcedPasswordChangeFailed dispatch, and if a store-selector-based
    // subscription here happened to run before changing$'s enable(), the
    // enable() call would revalidate the control and wipe this error right
    // back out (a real bug we hit — see forced-password-change ticket notes).
    this._actions$
      .pipe(
        ofType(ForcedPasswordChangeActions.forcedPasswordChangeFailed),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(action => {
        const fieldError = action.payload?.fieldError;
        const control = this.passwordForm.get('newPassword');
        this.newPasswordErrors.find(e => e.type === 'serverError').message =
          fieldError || '';

        if (fieldError) {
          // Exclusive: only the server's message should show, same as a
          // required/complexity error shows on its own.
          control.setErrors({ serverError: true });
        } else if (control.hasError('serverError')) {
          control.setErrors(null);
        }
      });
  }

  ngOnDestroy(): void {
    this._store.dispatch(
      ForcedPasswordChangeActions.forcedPasswordChangeCleared()
    );
  }

  // Public methods
  public onSubmit(): void {
    if (this.passwordForm.valid) {
      this._store.dispatch(
        ForcedPasswordChangeActions.forcedPasswordChangeRequested({
          payload: {
            challengeId: this._challengeId,
            newPassword: this.passwordForm.get('newPassword').value,
          },
        })
      );
    }
  }
}
